/**
 * Resolution of the API base URL.
 *
 * The backend mounts every route under the global `api/v1` prefix
 * (backend/src/main.ts), so the base URL the client talks to must always
 * carry that prefix. Two deployment mistakes used to strip it and send
 * `POST /auth/login` at the SPA origin — which nginx answers with a 405
 * from its static/SPA-fallback handler instead of ever reaching the API:
 *
 *  1. `VITE_API_BASE_URL=` (defined but empty). `??` only falls back on
 *     null/undefined, so the empty string won on the built bundle.
 *  2. `VITE_API_BASE_URL=https://ctms.example.com` — the origin without
 *     the `/api/v1` suffix.
 *  3. `VITE_API_BASE_URL=capctms….azurewebsites.net/api/v1` — the App Service
 *     hostname as the Azure portal displays it, with no `https://`. A value
 *     with no scheme is a RELATIVE url to axios and the browser: it resolves
 *     against the SPA origin, so every call went to
 *     `https://ctms.…/capctms….azurewebsites.net/api/v1/…` and the static
 *     host answered 405 — including the EDC `?token=` handoff, which then
 *     fell back to the login page.
 *
 * All three are repaired here so every caller shares one correct base.
 */

/** Path prefix the backend mounts all routes under. */
export const API_PREFIX = '/api/v1';

function resolveApiBase(raw: string | undefined): string {
  // Trailing slashes would double up against axios' leading-slash paths.
  const raw2 = (raw ?? '').trim().replace(/\/+$/, '');

  // Unset or blank → same-origin default, proxied by vite (dev) or nginx (prod).
  if (!raw2) return API_PREFIX;

  // A leading `/` is a deliberate same-origin path; anything else that names a
  // host must be absolute, so supply the scheme the operator left off. Without
  // this the value silently becomes a path segment under the SPA origin.
  const base =
    raw2.startsWith('/') || /^[a-z][a-z0-9+.-]*:\/\//i.test(raw2)
      ? raw2
      : `https://${raw2}`;

  // Already prefixed (origin + prefix, or a bare prefix) → use as-is.
  if (base === API_PREFIX || base.endsWith(API_PREFIX)) return base;

  if (import.meta.env.DEV) {
    console.warn(
      `[api] VITE_API_BASE_URL="${base}" is missing the ${API_PREFIX} prefix; appending it.`,
    );
  }
  return `${base}${API_PREFIX}`;
}

/** Base URL for all CTMS API calls — guaranteed to end with `/api/v1`. */
export const API_BASE_URL = resolveApiBase(import.meta.env.VITE_API_BASE_URL);
